import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { auth } from './firebase';

// The API requires a Firebase ID token on every request. Visitors who
// haven't signed in with Google still need one, so we establish an
// anonymous session in the background - Firebase restores a previously
// signed-in Google session automatically before the first
// onAuthStateChanged callback fires, so this only ever creates an
// anonymous user the first time a browser shows up with no session.
let sessionPromise = null;

export function ensureFirebaseSession() {
  if (!sessionPromise) {
    sessionPromise = new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe();
        if (user) {
          resolve(user);
          return;
        }
        signInAnonymously(auth)
          .then((credential) => resolve(credential.user))
          .catch((err) => {
            console.error('[Auth] anonymous sign-in failed', err);
            resolve(null);
          });
      });
    });
  }
  return sessionPromise;
}

export async function getAuthToken() {
  const user = auth.currentUser || await ensureFirebaseSession();
  return user ? user.getIdToken() : null;
}

export function isGoogleUser(user = auth.currentUser) {
  return !!user && !user.isAnonymous;
}

// Notifies `callback(isLoggedIn)` whenever the Google-identified state of
// the session changes (anonymous <-> Google, sign-out, etc). Returns the
// unsubscribe function.
export function onAuthChange(callback) {
  return onAuthStateChanged(auth, (user) => callback(isGoogleUser(user)));
}

// SHOP-209: apiFetch (api.js) calls notifyDemoModeRestricted() the moment
// any backend response carries the demo_mode_restricted error code, so a
// single high-level listener (App.js) can show the private-mode screen over
// the rest of the app. This is how a demo_mode block is actually discovered
// today - Firebase's client-side anonymous sign-in above never talks to our
// backend, so nothing blocks earlier, at bootstrap; the first real API call
// is what surfaces it.
const demoModeRestrictedListeners = new Set();

export function notifyDemoModeRestricted() {
  demoModeRestrictedListeners.forEach((listener) => listener());
}

// Returns the unsubscribe function, same convention as onAuthChange above.
export function onDemoModeRestricted(callback) {
  demoModeRestrictedListeners.add(callback);
  return () => demoModeRestrictedListeners.delete(callback);
}
